import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { ethers } from 'ethers';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import BethUtilityTokenABI from '../contracts/BethUtilityToken.json';
import CollateralVaultABI from '../contracts/CollateralVault.json';
import CreditLineContractABI from '../contracts/CreditLineContract.json';
import { MAINNET_CONTRACTS, MAINNET_CHAIN_ID, NETWORK_CONFIG } from '../config/contracts';

const Web3Context = createContext();

export const useWeb3 = () => useContext(Web3Context);

// WalletConnect Project ID - Get yours at https://cloud.walletconnect.com
const WALLETCONNECT_PROJECT_ID = 'a3cc41c451e7b9c0a8eb8e8b9a4fcf8c';

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chainId, setChainId] = useState(null);
  const [walletType, setWalletType] = useState(null); // 'metamask' | 'walletconnect'
  const [wcProvider, setWcProvider] = useState(null);
  
  // Token balances
  const [bethBalance, setBethBalance] = useState('0');
  const [availableBalance, setAvailableBalance] = useState('0');
  const [pledgedBalance, setPledgedBalance] = useState('0');
  
  // Vault position
  const [vaultPosition, setVaultPosition] = useState({
    collateralAmount: '0',
    creditUtilized: '0',
    currentLTV: '0',
    maxBorrowable: '0',
    isActive: false
  });
  
  // Credit line
  const [creditLine, setCreditLine] = useState({
    principal: '0',
    accruedInterest: '0',
    apr: '0',
    maturityDate: '0',
    isActive: false
  });

  // Initialize contracts with provider
  const initializeContracts = async (web3Provider, web3Signer, userAccount) => {
    const bethContract = new ethers.Contract(
      MAINNET_CONTRACTS.BETH_TOKEN,
      BethUtilityTokenABI.abi,
      web3Signer
    );

    const vaultContract = new ethers.Contract(
      MAINNET_CONTRACTS.COLLATERAL_VAULT,
      CollateralVaultABI.abi,
      web3Signer
    );

    const creditContract = new ethers.Contract(
      MAINNET_CONTRACTS.CREDIT_LINE,
      CreditLineContractABI.abi,
      web3Signer
    );

    setContracts({
      beth: bethContract,
      vault: vaultContract,
      credit: creditContract,
    });

    await fetchAllData(bethContract, vaultContract, creditContract, userAccount);
  };

  // Connect with MetaMask
  const connectMetaMask = async () => {
    if (!window.ethereum) {
      throw new Error('MetaMask not installed. Please install MetaMask or use WalletConnect for mobile wallets.');
    }

    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
      
      if (currentChainId !== MAINNET_CHAIN_ID) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: MAINNET_CHAIN_ID }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [NETWORK_CONFIG]
            });
          } else {
            throw switchError;
          }
        }
      }

      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      const web3Signer = web3Provider.getSigner();
      
      setProvider(web3Provider);
      setSigner(web3Signer);
      setAccount(accounts[0]);
      setChainId(MAINNET_CHAIN_ID);
      setWalletType('metamask');

      await initializeContracts(web3Provider, web3Signer, accounts[0]);
      return accounts[0];
    } catch (error) {
      console.error('MetaMask connection error:', error);
      throw error;
    } finally {
      setIsConnecting(false);
    }
  };

  // Connect with WalletConnect (Trust Wallet, Rainbow, etc.)
  const connectWalletConnect = async () => {
    setIsConnecting(true);
    try {
      // Create WalletConnect provider
      const wcEthereumProvider = await EthereumProvider.init({
        projectId: WALLETCONNECT_PROJECT_ID,
        chains: [1], // Ethereum Mainnet
        showQrModal: true,
        qrModalOptions: {
          themeMode: 'dark',
        },
        metadata: {
          name: 'Bethereum Private Wealth',
          description: 'Institutional-Grade Securities Finance Platform',
          url: window.location.origin,
          icons: ['https://avatars.githubusercontent.com/u/37784886']
        }
      });

      // Enable session (triggers QR modal)
      await wcEthereumProvider.enable();
      
      const accounts = wcEthereumProvider.accounts;
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts returned from WalletConnect');
      }

      const web3Provider = new ethers.providers.Web3Provider(wcEthereumProvider);
      const web3Signer = web3Provider.getSigner();
      
      setWcProvider(wcEthereumProvider);
      setProvider(web3Provider);
      setSigner(web3Signer);
      setAccount(accounts[0]);
      setChainId(MAINNET_CHAIN_ID);
      setWalletType('walletconnect');

      // Set up WalletConnect event listeners
      wcEthereumProvider.on('accountsChanged', handleAccountsChanged);
      wcEthereumProvider.on('chainChanged', handleChainChanged);
      wcEthereumProvider.on('disconnect', handleWcDisconnect);

      await initializeContracts(web3Provider, web3Signer, accounts[0]);
      return accounts[0];
    } catch (error) {
      console.error('WalletConnect connection error:', error);
      throw error;
    } finally {
      setIsConnecting(false);
    }
  };

  // Legacy connect function - defaults to MetaMask
  const connectWallet = async () => {
    return connectMetaMask();
  };

  const handleAccountsChanged = async (accounts) => {
    if (accounts.length > 0) {
      setAccount(accounts[0]);
      if (contracts?.beth && contracts?.vault && contracts?.credit) {
        await fetchAllData(contracts.beth, contracts.vault, contracts.credit, accounts[0]);
      }
    } else {
      disconnectWallet();
    }
  };

  const handleChainChanged = (newChainId) => {
    const chainIdNum = typeof newChainId === 'string' ? parseInt(newChainId, 16) : newChainId;
    setChainId(`0x${chainIdNum.toString(16)}`);
    if (chainIdNum !== 1) {
      console.warn('Please switch to Ethereum Mainnet');
    }
  };

  const handleWcDisconnect = () => {
    disconnectWallet();
  };

  const fetchAllData = async (bethContract, vaultContract, creditContract, address) => {
    try {
      // Fetch token balances
      const balance = await bethContract.balanceOf(address);
      const available = await bethContract.getAvailableBalance(address);
      const pledged = await bethContract.pledgedBalances(address);
      
      setBethBalance(ethers.utils.formatUnits(balance, 18));
      setAvailableBalance(ethers.utils.formatUnits(available, 18));
      setPledgedBalance(ethers.utils.formatUnits(pledged, 18));

      // Fetch vault position
      const position = await vaultContract.getPosition(address);
      setVaultPosition({
        collateralAmount: ethers.utils.formatUnits(position.collateralAmount, 18),
        creditUtilized: ethers.utils.formatUnits(position.creditUtilized, 18),
        currentLTV: position.currentLTV.toString(),
        maxBorrowable: ethers.utils.formatUnits(position.maxBorrowable, 18),
        isActive: position.isActive
      });

      // Fetch credit line
      const credit = await creditContract.getCreditLine(address);
      setCreditLine({
        principal: ethers.utils.formatUnits(credit.principal, 18),
        accruedInterest: ethers.utils.formatUnits(credit.accruedInterest, 18),
        apr: credit.apr.toString(),
        maturityDate: credit.maturityDate.toString(),
        isActive: credit.isActive
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const refreshData = async () => {
    if (contracts?.beth && contracts?.vault && contracts?.credit && account) {
      await fetchAllData(contracts.beth, contracts.vault, contracts.credit, account);
    }
  };

  const disconnectWallet = useCallback(async () => {
    // Disconnect WalletConnect if active
    if (wcProvider) {
      try {
        await wcProvider.disconnect();
      } catch (e) {
        console.error('WalletConnect disconnect error:', e);
      }
      setWcProvider(null);
    }
    
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setContracts(null);
    setWalletType(null);
    setBethBalance('0');
    setAvailableBalance('0');
    setPledgedBalance('0');
    setVaultPosition({ collateralAmount: '0', creditUtilized: '0', currentLTV: '0', maxBorrowable: '0', isActive: false });
    setCreditLine({ principal: '0', accruedInterest: '0', apr: '0', maturityDate: '0', isActive: false });
  }, [wcProvider]);

  // Token functions
  const approveBeth = async (spender, amount) => {
    if (!contracts?.beth) throw new Error('Contract not initialized');
    const amountWei = ethers.utils.parseUnits(amount.toString(), 18);
    const tx = await contracts.beth.approve(spender, amountWei);
    await tx.wait();
    return tx;
  };

  // Vault functions
  const depositCollateral = async (amount) => {
    if (!contracts?.vault || !contracts?.beth) throw new Error('Contracts not initialized');
    const amountWei = ethers.utils.parseUnits(amount.toString(), 18);
    
    // First approve vault to spend tokens
    const approveTx = await contracts.beth.approve(MAINNET_CONTRACTS.COLLATERAL_VAULT, amountWei);
    await approveTx.wait();
    
    // Then deposit
    const tx = await contracts.vault.deposit(amountWei);
    await tx.wait();
    await refreshData();
    return tx;
  };

  const withdrawCollateral = async (amount) => {
    if (!contracts?.vault) throw new Error('Contract not initialized');
    const amountWei = ethers.utils.parseUnits(amount.toString(), 18);
    const tx = await contracts.vault.withdraw(amountWei);
    await tx.wait();
    await refreshData();
    return tx;
  };

  // Credit functions
  const drawCredit = async (amount, termDays) => {
    if (!contracts?.credit) throw new Error('Contract not initialized');
    const amountWei = ethers.utils.parseUnits(amount.toString(), 18);
    const tx = await contracts.credit.drawCredit(amountWei, termDays);
    await tx.wait();
    await refreshData();
    return tx;
  };

  const repayCredit = async (amount) => {
    if (!contracts?.credit) throw new Error('Contract not initialized');
    const amountWei = ethers.utils.parseUnits(amount.toString(), 18);
    const tx = await contracts.credit.repayCredit(amountWei);
    await tx.wait();
    await refreshData();
    return tx;
  };

  // Listen for MetaMask account changes
  useEffect(() => {
    if (window.ethereum && walletType === 'metamask') {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, [contracts, walletType]);

  const value = {
    account,
    provider,
    signer,
    contracts,
    contractAddresses: MAINNET_CONTRACTS,
    isConnecting,
    chainId,
    isMainnet: chainId === MAINNET_CHAIN_ID,
    walletType,
    // Balances
    bethBalance,
    availableBalance,
    pledgedBalance,
    // Position data
    vaultPosition,
    creditLine,
    // Functions
    connectWallet,
    connectMetaMask,
    connectWalletConnect,
    disconnectWallet,
    refreshData,
    approveBeth,
    depositCollateral,
    withdrawCollateral,
    drawCredit,
    repayCredit,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

export default Web3Context;
